"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Image from "next/image";

import { cn, formatValue } from "@/lib/utils";
import { useCountdown } from "@/lib/utilHooks";
import { lastDayOfMonth } from "date-fns";
import { formatCompact } from "@/hooks/useYoutubeStats";
import { motion, useInView, useScroll, useTransform } from "framer-motion";

import Donut from "./Donut";

import { PageLeaderboardsSchema } from "@/sanity/pages/PageLeaderboardsTemplate";
import { exchanges } from "./data";
import { useExchangeStats, useTopUsers } from "@/hooks/useLeaderboardData";

import grid from "@/public/leaderboards/grid.svg";
import CornerChip from "@/components/global/ui/CornerChip";
import TransformCounter from "@/components/global/countdown/TransformCounter";

import logo from "@/public/favicon.svg";

const exchangesList = exchanges.flatMap((exchange) => exchange.name);

export default function PieChartSection({
  data,
  className,
}: {
  data: PageLeaderboardsSchema;
  className?: string;
}) {
  const chartWrapperRef = useRef<HTMLDivElement | any>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  // Fetch real exchange statistics from the platform API
  const { data: statsData, isLoading, error } = useExchangeStats();

  // Fetch top users for each exchange (limited to 5 for the tables)
  const bitunixTopUsers = useTopUsers("BITUNIX", 5);
  const pionexTopUsers = useTopUsers("PIONEX", 5);
  const phemexTopUsers = useTopUsers("PHEMEX", 5);

  // Merge real stats with static exchange metadata
  const exchangesWithRealData = useMemo(() => {
    if (!statsData) return exchanges;

    return exchanges.map((exchange) => {
      const realStats = statsData.find((stat) => stat.name === exchange.name);

      // Get top users for this exchange
      let topUsers = exchange.topUsers;
      if (exchange.name === "Bitunix" && bitunixTopUsers.data) {
        topUsers = bitunixTopUsers.data;
      } else if (exchange.name === "Pionex" && pionexTopUsers.data) {
        topUsers = pionexTopUsers.data;
      } else if (exchange.name === "Phemex" && phemexTopUsers.data) {
        topUsers = phemexTopUsers.data;
      }

      if (realStats) {
        return {
          ...exchange,
          totalPrize: realStats.totalPrize,
          totalWinners: realStats.totalWinners,
          usersCount: realStats.usersCount,
          topUsers: topUsers,
        };
      }
      return {
        ...exchange,
        topUsers: topUsers,
      };
    });
  }, [
    statsData,
    bitunixTopUsers.data,
    pionexTopUsers.data,
    phemexTopUsers.data,
  ]);

  // Track active exchange to prevent gaps during transitions
  const [activeExchange, setActiveExchange] = useState<string | null>(null);

  // Mobile detection for conditional chart transform
  const [isMobile, setIsMobile] = useState(false);

  const bitunixCardRef = useRef<HTMLDivElement | any>(null);
  const isBitunixInView = useInView(bitunixCardRef, {
    margin: "-30% 0px -30% 0px", // Trigger when card is 30% into viewport
  });
  const pionexCardRef = useRef<HTMLDivElement | any>(null);
  const isPionexInView = useInView(pionexCardRef, {
    margin: "-30% 0px -30% 0px",
  });
  const phemexCardRef = useRef<HTMLDivElement | any>(null);
  const isPhemexInView = useInView(phemexCardRef, {
    margin: "-30% 0px -30% 0px",
  });
  // Scroll-triggered transform for mobile: chart scrolls up with cards
  // Track the LAST card (Phemex) specifically to get precise timing
  // Start when last card approaches the chart position
  const { scrollYProgress } = useScroll({
    target: phemexCardRef,
    offset: ["start end", "start start"], // From card entering bottom of viewport to reaching top
  });

  // Map scroll progress to Y translation that moves gradually with scroll
  // Since rem is width-based and scales proportionally, calculate distance in rem
  // This ensures chart movement scales with viewport like all other elements
  const chartY = useTransform(scrollYProgress, (progress) => {
    if (progress < 0.35) return 0; // Don't move until 35% (slightly later)
    // Calculate movement in rem units for proportional scaling
    // Get current rem value (width-based) and calculate appropriate distance
    const remValue =
      typeof window !== "undefined"
        ? parseFloat(getComputedStyle(document.documentElement).fontSize)
        : 10;
    const distanceInRem = 65; // Move 65rem (35% slower than 100rem)
    return -((progress - 0.35) / 0.65) * distanceInRem * remValue;
  });

  // Update active exchange based on inView states
  useEffect(() => {
    // Order matches the card rendering order from top to bottom
    const inViewArray = [
      isBitunixInView, // First card (top)
      isPionexInView, // Second card
      isPhemexInView, // Last card (bottom)
    ];

    // Find the LAST (furthest down) card that is in view
    // This gives us the cumulative progress point
    const lastInViewIndex = inViewArray.lastIndexOf(true);

    // Only update if we found a card in view
    // This prevents deactivation during transition gaps
    if (lastInViewIndex !== -1) {
      // Use static exchanges array for consistent naming
      const newExchange = exchanges[lastInViewIndex]?.name;
      if (newExchange !== activeExchange) {
        setActiveExchange(newExchange);
      }
    }
  }, [isBitunixInView, isPionexInView, isPhemexInView, activeExchange]);

  const totalPrize = useMemo(() => {
    return exchangesWithRealData?.reduce((acc, exchange) => {
      return acc + exchange.totalPrize;
    }, 0);
  }, [exchangesWithRealData]);

  const getCurrentTotalPrize = useMemo(() => {
    if (!activeExchange) {
      return totalPrize;
    }

    // Find the index of the current exchange
    const currentIndex = exchangesWithRealData.findIndex(
      (exchange) => exchange.name === activeExchange,
    );

    if (currentIndex === -1) {
      return totalPrize;
    }

    // Calculate cumulative sum up to and including current exchange
    return exchangesWithRealData
      .slice(0, currentIndex + 1)
      .reduce((acc, exchange) => acc + exchange.totalPrize, 0);
  }, [activeExchange, totalPrize, exchangesWithRealData]);

  const deactivateAllExchanges = useCallback(() => {
    if (!chartWrapperRef.current) return;

    exchangesList.forEach((exchange) => {
      const element = chartWrapperRef.current.querySelector(
        `#${exchange.toLowerCase()}`,
      );
      if (element) {
        element.style.opacity = "0.2";
      }
    });
  }, []);

  const activateExchange = useCallback(
    (exchangeName: string | null) => {
      if (!exchangeName || !chartWrapperRef.current) {
        deactivateAllExchanges();
        return;
      }

      // Use static exchanges array for consistent ordering
      // This ensures activation works regardless of API data loading state
      const currentIndex = exchanges.findIndex(
        (exchange) => exchange.name === exchangeName,
      );

      if (currentIndex === -1) {
        deactivateAllExchanges();
        return;
      }

      // Deactivate all exchanges first
      deactivateAllExchanges();

      // Activate all exchanges up to and including the current one (cumulative)
      exchanges.slice(0, currentIndex + 1).forEach((exchange) => {
        const name = exchange.name.toLowerCase();
        const element = chartWrapperRef.current.querySelector(`#${name}`);
        if (element) {
          element.style.opacity = "1";
        }
      });
    },
    [deactivateAllExchanges],
  );

  // Activate exchanges based on the active exchange state
  useEffect(() => {
    activateExchange(activeExchange);
  }, [activeExchange, activateExchange]);

  // Initialize with first exchange active on mount
  useEffect(() => {
    // Small delay to ensure chartWrapperRef is ready
    const timer = setTimeout(() => {
      if (!activeExchange && chartWrapperRef.current) {
        setActiveExchange(exchanges[0]?.name || null);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Empty deps - only run on mount

  // Detect mobile viewport for conditional chart transform
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile(); // Check on mount
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const lastDayOfTheMonth = useMemo(() => {
    const today = new Date();
    const tempLastDayOfTheMonth = lastDayOfMonth(today);
    return new Date(
      tempLastDayOfTheMonth.getFullYear(),
      tempLastDayOfTheMonth.getMonth(),
      tempLastDayOfTheMonth.getDate(),
      23, // hours
      59, // minutes
      59, // seconds
      0,
    );
  }, []);

  const { days, hours, minutes, seconds } = useCountdown(
    lastDayOfTheMonth.toUTCString(),
  );

  return (
    <section className={cn("w-full relative bg-[#0A1A1D]", className)}>
      {/* background */}
      <Image
        src={grid}
        alt="grid"
        className="fixed left-0 top-0 w-full h-full object-cover pointer-events-none"
      />
      {/* end background */}

      <div className="w-full flex flex-col-reverse lg:flex-row justify-center gap-[6rem] lg:gap-[10rem] pb-[8rem] lg:py-[17rem] relative z-[1]">
        {/* cards */}
        <div
          ref={cardsContainerRef}
          className="flex flex-col gap-[40vh] lg:gap-[75vh] lg:pb-[20vh] px-[3rem] lg:px-0 relative z-[1]"
        >
          {exchangesWithRealData?.map((exchange, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                ease: [0, 0, 0.17, 1],
                duration: 1,
              }}
              className="sticky lg:relative top-[60vh] min-h-[35rem] h-[35rem] lg:h-fit"
              ref={
                exchange.name === "Bitunix"
                  ? bitunixCardRef
                  : exchange.name === "Pionex"
                    ? pionexCardRef
                    : exchange.name === "Phemex"
                      ? phemexCardRef
                      : undefined
              }
            >
              <CornerChip
                text={
                  exchange.name === "Bitunix"
                    ? "1ST PLACE"
                    : exchange.name === "Pionex"
                      ? "2ND PLACE"
                      : "3RD PLACE"
                }
                className="hidden lg:flex w-[15rem] text-black mb-[-.125rem]"
                style={{ background: exchange.color }}
              />
              <CornerChip
                text={
                  exchange.name === "Bitunix"
                    ? "1ST PLACE"
                    : exchange.name === "Pionex"
                      ? "2ND PLACE"
                      : "3RD PLACE"
                }
                className="lg:hidden w-[15rem] text-black mb-[-.125rem]"
                style={{
                  background: exchange.color,
                  marginLeft: index * 15 + "rem",
                }}
              />
              <div
                className="bg-[#112628] p-[2rem] lg:p-[3rem] flex flex-col lg:gap-[15rem] w-full lg:w-[47rem] border-t-[.25rem] relative"
                style={{ borderTopColor: exchange.color }}
              >
                <div className="flex flex-col gap-[2rem]">
                  <span
                    className="font-geistmono text-[2.5rem] uppercase"
                    style={{
                      color: exchange.color,
                    }}
                  >
                    Exchange {index + 1}
                  </span>
                  <div className="flex items-center gap-[2rem]">
                    <span className="w-full text-white !font-geistmono text-[4rem] font-[500]">
                      {formatCompact(exchange.totalPrize)} USD
                    </span>
                    <span
                      className="w-full lg:w-[90%] text-white !font-geistmono text-[2.25rem] font-[500] px-[1.5rem] py-[1rem] flex justify-center items-center"
                      style={{
                        color: exchange.color,
                        background: exchange.color + "1a",
                      }}
                    >
                      {formatValue(exchange.usersCount, 0)} USERS
                    </span>
                  </div>
                  <hr className="my-[2rem] lg:mb-[2rem] lg:mt-0 border-t-white/10" />
                </div>
                <div>
                  <p className="text-white/90 !font-geist text-[2.25rem] lg:text-[2.25rem] mb-0">
                    {formatValue(exchange.totalPrize, 0)} USD{" "}
                    <span className="text-white/50">total prizes</span>
                  </p>
                  <p className="text-white/90 !font-geist text-[2.25rem] lg:text-[2.25rem] mb-0">
                    {formatValue(exchange.totalWinners, 0)}{" "}
                    <span className="text-white/50">total winners</span>
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {/* end cards */}

        {/* chart */}
        <motion.div
          style={{ y: isMobile ? chartY : 0 }}
          className="h-fit sticky top-[5rem] lg:top-[17rem]"
        >
          <div
            ref={chartWrapperRef}
            className="relative mx-auto px-[2rem] lg:px-0"
          >
            <Donut className="w-full lg:w-[85rem] min-w-full lg:min-w-[85rem] h-full lg:h-[85rem] min-h-full lg:min-h-[85rem]" />
            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-full max-w-[47rem] flex flex-col items-center justify-center">
              {/* prize pool */}
              {!activeExchange && (
                <span className="!font-geistmono leading-[100%] text-center text-white/50 mb-1">
                  Total
                </span>
              )}
              <div className="flex lg:mb-[4rem]">
                <span className="!font-geistmono leading-[100%] text-[7rem] lg:text-[12rem] text-center text-white/50">
                  $
                </span>
                {formatValue(getCurrentTotalPrize ?? 0, 0)
                  .split("")
                  .map((digit, index) => {
                    if (isNaN(Number(digit))) {
                      return (
                        <span
                          key={index + 20}
                          className="!font-geistmono leading-[100%] text-[7rem] lg:text-[12rem] text-center text-white"
                        >
                          {digit}
                        </span>
                      );
                    } else {
                      return (
                        <TransformCounter
                          key={index + 20}
                          digit={Number(digit)}
                        />
                      );
                    }
                  })}
              </div>
              {/* end prize pool */}
              {/* countdown */}
              <div className="lg:bg-[#112628] p-[3rem]">
                <h4 className="text-white !font-geistmono text-[2.25rem] lg:text-[2.25rem] text-center">
                  Prize Pool
                </h4>

                <p className="text-white/60 !font-geistmono text-[2rem] lg:text-[2.25rem] text-center mb-0 px-[2rem] lg:px-0">
                  {data?.settings?.language === "en"
                    ? "The largest Community Sashback in the Trading Scene"
                    : "der größte Community Cashback in der Trading Szene"}
                </p>

                <hr className="hidden lg:block my-[1.5rem] border-t-white/10" />

                <div className="hidden lg:grid grid-cols-4 gap-[1rem]">
                  {/*  */}
                  <div className="flex flex-col justify-center items-center gap-[1rem]">
                    <span className="font-[500] !text-white/40 text-[1.75rem]">
                      DAYS
                    </span>
                    <div className="w-full flex gap-[.25rem] justify-between items-center">
                      <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                        {days}
                      </span>
                    </div>
                  </div>
                  {/*  */}
                  {/*  */}
                  <div className="flex flex-col justify-center items-center gap-[1rem]">
                    <span className="font-[500] !text-white/40 text-[1.75rem]">
                      HOURS
                    </span>
                    <div className="w-full flex gap-[.25rem] justify-between items-center">
                      <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                        {hours}
                      </span>
                    </div>
                  </div>
                  {/*  */}
                  {/*  */}
                  <div className="flex flex-col justify-center items-center gap-[1rem]">
                    <span className="font-[500] !text-white/40 text-[1.75rem]">
                      MINUTES
                    </span>
                    <div className="w-full flex gap-[.25rem] justify-between items-center">
                      <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                        {minutes}
                      </span>
                    </div>
                  </div>
                  {/*  */}
                  {/*  */}
                  <div className="flex flex-col justify-center items-center gap-[1rem]">
                    <span className="font-[500] !text-white/40 text-[1.75rem]">
                      SECONDS
                    </span>
                    <div className="w-full flex gap-[.25rem] justify-between items-center">
                      <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                        {seconds}
                      </span>
                    </div>
                  </div>
                  {/*  */}
                </div>
              </div>
              {/* end countdown */}
            </div>
          </div>
        </motion.div>
        {/* end chart */}

        {/* tables */}
        <div className="hidden lg:flex flex-col gap-[84vh] pt-[80vh] pb-[20vh] relative">
          {exchangesWithRealData?.map((exchange, index) => (
            <motion.div
              key={index + 10}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                ease: [0, 0, 0.17, 1],
                duration: 1,
              }}
              className="sticky top-[50vh]"
            >
              <div className="bg-[#112628] p-[2rem] w-[52rem]">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="text-white/50 text-[1.75rem] font-[500]">
                      <th></th>
                      <th className="text-center">#</th>
                      <th className="text-start">NAME</th>
                      <th className="text-end">TRADING VOLUME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exchange?.topUsers.map((user, index) => (
                      <tr
                        key={index + 100}
                        className="group border-b border-b-white/10 last:border-none"
                      >
                        <td className="text-white/50 text-[1.75rem] font-[500] group-hover:text-white transition-colors py-[1rem]">
                          <Image
                            src={user.avatarURL}
                            width={32}
                            height={32}
                            alt="avatar"
                            className="rounded-full overflow-clip opacity-80 group-hover:opacity-100 transition-opacity min-w-[4rem] min-h-[4rem] max-w-[4rem] max-h-[4rem] lg:min-w-[5rem] lg:min-h-[5rem] lg:max-w-[5rem] lg:max-h-[5rem] border border-white/20"
                          />
                        </td>
                        <td className="text-white/50 text-[1.75rem] font-[500] group-hover:text-white transition-colors text-center px-[2rem] py-[1rem]">
                          {index + 1}
                        </td>
                        <td className="text-white/50 text-[1.75rem] font-[500] group-hover:text-white transition-colors py-[1rem] leading-[100%]">
                          {user.name}
                        </td>
                        <td className="text-white/50 text-[1.75rem] font-[500] group-hover:text-white transition-colors text-end py-[1rem]">
                          {/* $ {formatValue(user.tradingVolume, 0)} */}
                          $ ***,***.***
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          ))}
        </div>
        {/* end tables */}

        {/* mobile countdown */}
        <div className="lg:hidden bg-[#112628] p-[2rem]">
          <div className="grid grid-cols-4 gap-[1rem]">
            {/*  */}
            <div className="flex flex-col justify-center items-center gap-[1rem]">
              <div className="w-full flex gap-[.25rem] justify-between items-center">
                <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                  {days}
                </span>
              </div>
              <span className="font-[500] !text-white/40 text-[1.75rem]">
                DAYS
              </span>
            </div>
            {/*  */}
            {/*  */}
            <div className="flex flex-col justify-center items-center gap-[1rem]">
              <div className="w-full flex gap-[.25rem] justify-between items-center">
                <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                  {hours}
                </span>
              </div>
              <span className="font-[500] !text-white/40 text-[1.75rem]">
                HOURS
              </span>
            </div>
            {/*  */}
            {/*  */}
            <div className="flex flex-col justify-center items-center gap-[1rem]">
              <div className="w-full flex gap-[.25rem] justify-between items-center">
                <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                  {minutes}
                </span>
              </div>
              <span className="font-[500] !text-white/40 text-[1.75rem]">
                MINUTES
              </span>
            </div>
            {/*  */}
            {/*  */}
            <div className="flex flex-col justify-center items-center gap-[1rem]">
              <div className="w-full flex gap-[.25rem] justify-between items-center">
                <span className="w-full h-full font-[500] text-white/70 bg-white/5 text-[2.25rem] flex items-center justify-center py-[1.25rem]">
                  {seconds}
                </span>
              </div>
              <span className="font-[500] !text-white/40 text-[1.75rem]">
                SECONDS
              </span>
            </div>
            {/*  */}
          </div>
        </div>
        {/* end mobile countdown */}
      </div>
    </section>
  );
}
